import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { motion } from 'framer-motion';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ConversionMetricsProps {
  totalApplications: number;
  interviews: number;
  offers: number;
}

const ConversionMetrics: React.FC<ConversionMetricsProps> = ({
  totalApplications,
  interviews,
  offers,
}) => {
  const interviewRate = totalApplications ? (interviews / totalApplications) * 100 : 0;
  const offerRate = totalApplications ? (offers / totalApplications) * 100 : 0;

  const chartData = {
    labels: ['Applications Submitted', 'Interviews Secured', 'Offers Received'],
    datasets: [{
      label: 'Your Job Search Journey',
      data: [totalApplications, interviews, offers],
      backgroundColor: ['rgba(99, 102, 241, 0.8)', 'rgba(59, 130, 246, 0.8)', 'rgba(16, 185, 129, 0.8)'],
      borderColor: ['rgb(99, 102, 241)', 'rgb(59, 130, 246)', 'rgb(16, 185, 129)'],
      borderWidth: 2,
      borderRadius: 8,
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          padding: 20,
          font: {
            size: 12,
            weight: 'bold' as const,
          }
        }
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold' as const
        },
        bodyFont: {
          size: 13
        },
        cornerRadius: 6,
        displayColors: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          precision: 0,
          font: {
            size: 12
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          }
        }
      }
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-xl shadow-lg p-6"
    >
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Your Success Pipeline</h2>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-6 text-white">
          <h3 className="text-lg font-semibold mb-1">Offer Success Rate</h3>
          <div className="text-3xl font-bold mb-2 whitespace-nowrap">
            {offerRate.toFixed(1)}%
          </div>
          <p className="text-indigo-100 text-sm">
            of applications<br />
            lead to offers
          </p>
        </div>

        <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl p-6 text-white">
          <h3 className="text-lg font-semibold mb-1">Interview Success Rate</h3>
          <div className="text-3xl font-bold mb-2 whitespace-nowrap">
            {interviewRate.toFixed(1)}%
          </div>
          <p className="text-emerald-100 text-sm">
            of applications<br />
            lead to interviews
          </p>
        </div>
      </div>

      <div className="h-[300px]">
        <Bar data={chartData} options={options} />
      </div>
    </motion.div>
  );
};

export default ConversionMetrics;